import { useCallback } from 'react';
import { Box, Flex, SimpleGrid, Stack } from '@mantine/core';
import moment from 'moment';
import { useMask } from '@react-input/mask';

import { CreditRequestStep, CreditResponse, ValidationRules } from 'types';
import { Button, CheckBox, DatePicker, InputLabel, MonthPicker, SectionLabel, Select, Text, Textarea, TextInput, TimePicker, YesNoRadioButton } from 'components';
import { AppConstants } from '../../../../constants';
import CreditRequestPersonalChildren from './personal/children';
import { AppUtils } from 'utils';

const { CREDIT_REQUEST_FORM_PADDING, MASK_SWISS_PHONE_NUMBER } = AppConstants;

const PersonalInformationStep = ({
  form,
  isEditable,
  onSubmit,
  options,
  setValues,
  t,
}: CreditRequestStep) => {
  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });

  const onSubmitWrapper = useCallback(() => {
    if (isEditable && !isFormValid()) {
      return;
    }
    onSubmit();
  }, [form.values, options, isEditable]);

  const isFormValid = useCallback(() => {
    let isValid = true;

    const runValidation = (name: keyof CreditResponse, value: any, rules: ValidationRules) => {
      const result = AppUtils.validateField(name, value, rules, form, t);
      if (!result) {
        isValid = false;
      }
    };

    runValidation('gender', form.values.gender, { required: true });
    runValidation('birthDate', form.values.birthDate, { required: true });
    runValidation('nationality', form.values.nationality, { required: true });
    if (form.values.nationality === 'OTHER') {
      runValidation('visaType', form.values.visaType, { required: true });
      runValidation('visaFrom', form.values.visaFrom, { required: true });
    }

    runValidation('postal', form.values.postal, { required: true });
    runValidation('city', form.values.city, { required: true });
    runValidation('street', form.values.street, { required: true });
    runValidation('houseNumber', form.values.houseNumber, { required: true });
    runValidation('livingFrom', form.values.livingFrom, { required: true });
    if (form.values.livingFrom && AppUtils.isYoungerThanTwelveMonths(form.values.livingFrom)) {
      runValidation('previousPostal', form.values.previousPostal, { required: true });
      runValidation('previousCity', form.values.previousCity, { required: true });
      runValidation('previousStreet', form.values.previousStreet, { required: true });
      runValidation('previousHouseNumber', form.values.previousHouseNumber, { required: true });
    }

    if (form.values.maritalStatus === 'MARRIED' && form.values.partnerOnSameAddress && form.values.partnerHasIncome && form.values.partnerIncludeInApplication) {
      runValidation('partnerBirthDate', form.values.partnerBirthDate, { required: true });
      runValidation('partnerNationality', form.values.partnerNationality, { required: true });
      if (form.values.partnerEmail && form.values.user.email === form.values.partnerEmail) {
        isValid = false;
        form.setFieldError('partnerEmail', t('input.email.sameAsUser'));
      }
      if (form.values.partnerPhoneNumber && form.values.user.phoneNumber === form.values.partnerPhoneNumber) {
        isValid = false;
        form.setFieldError('partnerPhoneNumber', t('input.phoneNumber.sameAsUser'));
      }
      if (form.values.partnerNationality === 'OTHER') {
        runValidation('partnerVisaType', form.values.partnerVisaType, { required: true });
        runValidation('partnerVisaFrom', form.values.partnerVisaFrom, { required: true });
      }
    }

    return isValid;
  }, [form.values, options]);

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }}>
      <Stack p={CREDIT_REQUEST_FORM_PADDING}>
        <TextInput
          label={t('input.fullName.label')}
          defaultValue={form.values.user.fullName}
          disabled={true}
        />
        <Select
          label={t('input.gender.label')}
          data={['MALE', 'FEMALE', 'OTHER'].map(el => (
            { value: el, label: t(`input.gender.options.${el}`) }
          ))}
          key={form.key('gender')}
          {...form.getInputProps('gender')}
          clearable={true}
          required={true}
          disabled={!isEditable}
        />
        <DatePicker
          label={t('input.birthDate.label')}
          placeholder={t('input.date.placeholder')}
          key={form.key('birthDate')}
          {...form.getInputProps('birthDate')}
          minDate={moment().subtract(65, 'years').calendar()}
          maxDate={moment().subtract(18, 'years').calendar()}
          required={true}
          disabled={!isEditable}
        />
        <TextInput
          label={t('input.email.label')}
          defaultValue={form.values.user.email}
          disabled={true}
        />
        <TextInput
          label={t('input.phoneNumber.label')}
          defaultValue={form.values.user.countryCode + ' ' + form.values.user.phoneNumber}
          disabled={true}
        />
        <Box>
          <InputLabel label={t('input.workdayAvailability.label')} />
          <Flex align={'center'} justify={'space-evenly'}>
            <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>{t('input.from')}</Text>
            <TimePicker
              key={form.key('workdayAvailabilityFrom')}
              {...form.getInputProps('workdayAvailabilityFrom')}
              min={options['workdayAvailability']?.min}
              max={options['workdayAvailability']?.max}
              w={'30%'}
              disabled={!isEditable}
            />
            <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>{t('input.to')}</Text>
            <TimePicker
              key={form.key('workdayAvailabilityTo')}
              {...form.getInputProps('workdayAvailabilityTo')}
              min={options['workdayAvailability']?.min}
              max={options['workdayAvailability']?.max}
              w={'30%'}
              disabled={!isEditable}
            />
          </Flex>
        </Box>
        <Flex align={'center'}>
          <CheckBox
            key={form.key('saturdayAvailability')}
            {...form.getInputProps('saturdayAvailability')}
            checked={form.values.saturdayAvailability}
            disabled={!isEditable}
          />
          <Text ml={'xs'} c={!isEditable ? 'gray' : undefined}>{t('input.saturdayAvailability.label')}</Text>
        </Flex>
        {form.values.saturdayAvailability ?
          <Box>
            <InputLabel label={t('input.saturdayAvailability.label')} />
            <Flex align={'center'} justify={'space-evenly'}>
              <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>{t('input.from')}</Text>
              <TimePicker
                key={form.key('saturdayAvailabilityFrom')}
                {...form.getInputProps('saturdayAvailabilityFrom')}
                min={options['saturdayAvailability']?.min}
                max={options['saturdayAvailability']?.max}
                w={'30%'}
                disabled={!isEditable}
              />
              <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>{t('input.to')}</Text>
              <TimePicker
                key={form.key('saturdayAvailabilityTo')}
                {...form.getInputProps('saturdayAvailabilityTo')}
                min={options['saturdayAvailability']?.min}
                max={options['saturdayAvailability']?.max}
                w={'30%'}
                disabled={!isEditable}
              />
            </Flex>
          </Box>
          : undefined}
        <Select
          label={t('input.nationality.label')}
          data={options['nationality'].map(el => (
            { value: el, label: t(`input.nationality.options.${el}`) }
          ))}
          key={form.key('nationality')}
          {...form.getInputProps('nationality')}
          clearable={true}
          required={true}
          disabled={!isEditable}
        />
        {form.values.nationality === 'OTHER' ?
          <>
            <Select
              label={t('input.visaType.label')}
              data={options['visaType'].map(el => (
                { value: el, label: t(`input.visaType.options.${el}`) }
              ))}
              key={form.key('visaType')}
              {...form.getInputProps('visaType')}
              clearable={true}
              required={true}
              disabled={!isEditable}
            />
            <MonthPicker
              label={t('input.visaFrom.label')}
              key={form.key('visaFrom')}
              {...form.getInputProps('visaFrom')}
              minDate={moment().subtract(20, 'years').calendar()}
              maxDate={new Date()}
              required={true}
              disabled={!isEditable}
            />
          </>
          : undefined}
        <SectionLabel label={t('credit.new.address')} variant={'green'} />
        <Box>
          <Flex direction={'row'} justify={'space-between'}>
            <TextInput
              label={t('input.postal.label')}
              key={form.key('postal')}
              {...form.getInputProps('postal')}
              placeholder={t('input.postal.label')}
              disabled={!isEditable}
              required={true}
              maxLength={4}
            />
            <TextInput
              label={t('input.city.label')}
              key={form.key('city')}
              {...form.getInputProps('city')}
              placeholder={t('input.city.label')}
              disabled={!isEditable}
              required={true}
            />
          </Flex>
        </Box>
        <Box>
          <Flex direction={'row'} justify={'space-between'}>
            <TextInput
              label={t('input.street.label')}
              key={form.key('street')}
              {...form.getInputProps('street')}
              placeholder={t('input.street.label')}
              disabled={!isEditable}
              required={true}
            />
            <TextInput
              label={t('input.houseNumber.label')}
              key={form.key('houseNumber')}
              {...form.getInputProps('houseNumber')}
              placeholder={t('input.houseNumber.label')}
              disabled={!isEditable}
              required={true}
            />
          </Flex>
        </Box>
        <MonthPicker
          label={t('input.livingFrom.label')}
          key={form.key('livingFrom')}
          {...form.getInputProps('livingFrom')}
          minDate={form.values.birthDate}
          maxDate={new Date()}
          required={true}
          disabled={!isEditable}
        />
        {form.values.livingFrom && AppUtils.isYoungerThanTwelveMonths(new Date(form.values.livingFrom)) ?
          <>
            <SectionLabel label={t('credit.new.previousAddress')} variant={'green'} />
            <Box>
              <Flex direction={'row'} justify={'space-between'}>
                <TextInput
                  label={t('input.postal.label')}
                  key={form.key('previousPostal')}
                  {...form.getInputProps('previousPostal')}
                  placeholder={t('input.postal.label')}
                  disabled={!isEditable}
                  required={true}
                  maxLength={4}
                />
                <TextInput
                  label={t('input.city.label')}
                  key={form.key('previousCity')}
                  {...form.getInputProps('previousCity')}
                  placeholder={t('input.city.label')}
                  disabled={!isEditable}
                  required={true}
                />
              </Flex>
            </Box>
            <Box>
              <Flex direction={'row'} justify={'space-between'}>
                <TextInput
                  label={t('input.street.label')}
                  key={form.key('previousStreet')}
                  {...form.getInputProps('previousStreet')}
                  placeholder={t('input.street.label')}
                  disabled={!isEditable}
                  required={true}
                />
                <TextInput
                  label={t('input.houseNumber.label')}
                  key={form.key('previousHouseNumber')}
                  {...form.getInputProps('previousHouseNumber')}
                  placeholder={t('input.houseNumber.label')}
                  disabled={!isEditable}
                  required={true}
                />
              </Flex>
            </Box>
          </>
          : undefined}
      </Stack>
      <Stack p={CREDIT_REQUEST_FORM_PADDING}>
        <CreditRequestPersonalChildren form={form} setValues={setValues} t={t} disabled={!isEditable} />
        <Select
          label={t('input.maritalStatus.label')}
          data={options['maritalStatus'].map(el => (
            { value: el, label: t(`input.maritalStatus.options.${el}`) }
          ))}
          key={form.key('maritalStatus')}
          {...form.getInputProps('maritalStatus')}
          clearable={true}
          disabled={!isEditable}
        />
        {form.values.maritalStatus === 'MARRIED' ?
          <>
            <YesNoRadioButton
              name={'partnerOnSameAddress'}
              label={t('input.partnerOnSameAddress.label')}
              t={t}
              form={form}
              disabled={!isEditable}
            />
            <YesNoRadioButton
              name={'partnerHasIncome'}
              label={t('input.partnerHasIncome.label')}
              t={t}
              form={form}
              disabled={!isEditable}
            />
            {form.values.partnerHasIncome && form.values.partnerOnSameAddress ?
              <>
                <YesNoRadioButton
                  name={'partnerIncludeInApplication'}
                  label={t('input.partnerIncludeInApplication.label')}
                  t={t}
                  form={form}
                  disabled={!isEditable}
                />
                {form.values.partnerIncludeInApplication ?
                  <>
                    <SectionLabel label={t('credit.new.partner')} variant={'green'} />
                    <TextInput
                      label={t('input.partnerFirstName.label')}
                      key={form.key('partnerFirstName')}
                      {...form.getInputProps('partnerFirstName')}
                      disabled={!isEditable}
                    />
                    <TextInput
                      label={t('input.partnerLastName.label')}
                      key={form.key('partnerLastName')}
                      {...form.getInputProps('partnerLastName')}
                      disabled={!isEditable}
                    />
                    <Select
                      label={t('input.partnerGender.label')}
                      data={['MALE', 'FEMALE', 'OTHER'].map(el => (
                        { value: el, label: t(`input.gender.options.${el}`) }
                      ))}
                      key={form.key('partnerGender')}
                      {...form.getInputProps('partnerGender')}
                      clearable={true}
                      disabled={!isEditable}
                    />
                    <DatePicker
                      label={t('input.partnerBirthDate.label')}
                      placeholder={t('input.date.placeholder')}
                      key={form.key('partnerBirthDate')}
                      {...form.getInputProps('partnerBirthDate')}
                      minDate={moment().subtract(65, 'years').calendar()}
                      maxDate={moment().subtract(18, 'years').calendar()}
                      required={true}
                      disabled={!isEditable}
                    />
                    <TextInput
                      label={t('input.partnerEmail.label')}
                      placeholder={t('input.email.placeholder')}
                      key={form.key('partnerEmail')}
                      {...form.getInputProps('partnerEmail')}
                      disabled={!isEditable}
                    />
                    <TextInput
                      label={t('input.partnerPhoneNumber.label')}
                      placeholder={t('input.phoneNumber.placeholder')}
                      key={form.key('partnerPhoneNumber')}
                      {...form.getInputProps('partnerPhoneNumber')}
                      disabled={!isEditable}
                      ref={phoneNumberRef}
                    />
                    <Box>
                      <InputLabel label={t('input.partnerWorkdayAvailability.label')} />
                      <Flex align={'center'} justify={'space-evenly'}>
                        <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>{t('input.from')}</Text>
                        <TimePicker
                          key={form.key('partnerWorkdayAvailabilityFrom')}
                          {...form.getInputProps('partnerWorkdayAvailabilityFrom')}
                          min={options['workdayAvailability']?.min}
                          max={options['workdayAvailability']?.max}
                          w={'30%'}
                          disabled={!isEditable}
                        />
                        <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>{t('input.to')}</Text>
                        <TimePicker
                          key={form.key('partnerWorkdayAvailabilityTo')}
                          {...form.getInputProps('partnerWorkdayAvailabilityTo')}
                          min={options['workdayAvailability']?.min}
                          max={options['workdayAvailability']?.max}
                          w={'30%'}
                          disabled={!isEditable}
                        />
                      </Flex>
                    </Box>
                    <Flex align={'center'}>
                      <CheckBox
                        key={form.key('partnerSaturdayAvailability')}
                        {...form.getInputProps('partnerSaturdayAvailability')}
                        checked={form.values.partnerSaturdayAvailability}
                        disabled={!isEditable}
                      />
                      <Text ml={'xs'} c={!isEditable ? 'gray' : undefined}>{t('input.partnerSaturdayAvailability.label')}</Text>
                    </Flex>
                    {form.values.partnerSaturdayAvailability ?
                      <Box>
                        <InputLabel label={t('input.partnerSaturdayAvailability.label')} />
                        <Flex align={'center'} justify={'space-evenly'}>
                          <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>{t('input.from')}</Text>
                          <TimePicker
                            key={form.key('partnerSaturdayAvailabilityFrom')}
                            {...form.getInputProps('partnerSaturdayAvailabilityFrom')}
                            min={options['saturdayAvailability']?.min}
                            max={options['saturdayAvailability']?.max}
                            w={'30%'}
                            disabled={!isEditable}
                          />
                          <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>{t('input.to')}</Text>
                          <TimePicker
                            key={form.key('partnerSaturdayAvailabilityTo')}
                            {...form.getInputProps('partnerSaturdayAvailabilityTo')}
                            min={options['saturdayAvailability']?.min}
                            max={options['saturdayAvailability']?.max}
                            w={'30%'}
                            disabled={!isEditable}
                          />
                        </Flex>
                      </Box>
                      : undefined}
                    <Select
                      label={t('input.partnerNationality.label')}
                      data={options['nationality'].map(el => (
                        { value: el, label: t(`input.nationality.options.${el}`) }
                      ))}
                      key={form.key('partnerNationality')}
                      {...form.getInputProps('partnerNationality')}
                      clearable={true}
                      required={true}
                      disabled={!isEditable}
                    />
                    {form.values.partnerNationality === 'OTHER' ?
                      <>
                        <Select
                          label={t('input.partnerVisaType.label')}
                          data={options['visaType'].map(el => (
                            { value: el, label: t(`input.visaType.options.${el}`) }
                          ))}
                          key={form.key('partnerVisaType')}
                          {...form.getInputProps('partnerVisaType')}
                          clearable={true}
                          required={true}
                          disabled={!isEditable}
                        />
                        <MonthPicker
                          label={t('input.partnerVisaFrom.label')}
                          key={form.key('partnerVisaFrom')}
                          {...form.getInputProps('partnerVisaFrom')}
                          minDate={moment().subtract(20, 'years').calendar()}
                          maxDate={new Date()}
                          required={true}
                          disabled={!isEditable}
                        />
                      </>
                      : undefined}
                  </>
                  : undefined}
              </>
              : undefined}
          </>
          : undefined}
        <Textarea
          label={t('input.comment.label')}
          key={form.key('comment2')}
          {...form.getInputProps('comment2')}
          info={t('input.comment.info.message')}
          disabled={!isEditable}
        />
        <Button onClick={onSubmitWrapper}>
          {t('action.next')}
        </Button>
      </Stack>
    </SimpleGrid>
  )
}

export default PersonalInformationStep;